import {
    useEffect,
    useState,
} from 'react';

// FNP-918: module-scoped filter store (checklist §7; FNP-815).
//
// Filters live here rather than in component state so the FilterDrawer and the
// table filter the SAME list. Keyword and filters COMPOSE — they are applied
// independently in usePaymentMethods, so applying both narrows rather than
// resets.
//
// ⚠️ These filters are applied CLIENT-SIDE, over the pages loaded so far.
// ⚠️ UNLIKE the other lists in this app, `PaymentMethodsFilterInput` offers a
// REAL server-side filter: `{customerId: String!, type: PaymentMethodType}`.
// `type` is therefore sent to the API and lands in the queryKey, so switching it
// refetches rather than narrowing the loaded window. Server-side filtering is
// preferred exactly where the API supports it (§9) — the match count then means
// something, because the server examined every row rather than the loaded page.
//
// Anything NOT in that input (card brand, last4) can only be matched
// client-side, over the pages loaded so far. See usePaymentMethods.ts.
export type PaymentMethodsFilters = Record<string, string>;

let globalFilters: PaymentMethodsFilters = {};
const filterListeners: Set<(filters: PaymentMethodsFilters) => void> = new Set();

const notifyFilterChange = (filters: PaymentMethodsFilters) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

/** Test seam: resets the store so filters do not leak between test cases. */
export const resetPaymentMethodsFilter = () => notifyFilterChange({});

export const usePaymentMethodsFilter = () => {
    const [filters, setFilters] = useState<PaymentMethodsFilters>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: PaymentMethodsFilters) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    // The badge on FilterRecordsButton — a filter you cannot see is a filter you
    // forget is on (§7). Empty-string values are treated as unset so a cleared
    // field does not keep inflating the count.
    const activeFilterCount = Object.values(filters)
                                    .filter(value => value !== '' && value !== undefined)
                                    .length;

    return {
        filters,
        activeFilterCount,
        handleSetFilters: notifyFilterChange,
        handleClearFilters: () => notifyFilterChange({}),
    };
};
